<template>
    <div class="segment-metrics-panel">
        <div
            v-if="showGuideEligibility"
            class="guide-eligibility">
            <h3 class="guide-eligibility-label">
                {{ $t('segmentMetricsPanel.Guides eligible') }}
            </h3>
            <div class="guide-eligibility-wrapper">
                <pendo-icon
                    type="circle"
                    size="8"
                    aria-hidden="true"
                    :fill="guideStatusColor"
                    :stroke="guideStatusColor" />
                <span class="guide-eligibility-text"> {{ guideEligibilityText }}</span>
                <pendo-icon
                    v-if="!guideEligibility.eligible"
                    v-pendo-tooltip="{
                        arrow: true,
                        multiline: true,
                        content: guideEligibility.reason,
                        trigger: 'hover focus'
                    }"
                    :aria-label="
                        $t('segmentMetricsPanel.Not guides eligible', {
                            guideEligibilityReason: guideEligibility.reason
                        })
                    "
                    tabindex="0"
                    type="info"
                    size="14" />
            </div>
        </div>
        <div
            v-if="!showDependenciesTab"
            class="eligible-visitors-wrapper"
            :class="{ 'top-padding': !showGuideEligibility }">
            <span class="eligible-visitors-label">
                {{ eligibleVisitorsLabel }}
            </span>
            <div class="eligible-visitors-list-wrapper">
                <eligible-visitors-list
                    :segment="segment"
                    :is-valid="isValid"
                    @eligible-visitors-label="eligibleVisitorsLabel = $event"
                    @eligible-visitors-updated="$emit('eligibleVisitorsUpdated', $event)" />
            </div>
        </div>
        <pendo-tabs
            v-if="showDependenciesTab"
            v-model="activeTab"
            class="segment-metrics-panel--tabs"
            :tabs="tabOptions"
            theme="app">
            <template #eligibleVisitors>
                <div class="eligible-visitors-list-wrapper">
                    <eligible-visitors-list
                        :segment="segment"
                        :is-valid="isValid"
                        @eligible-visitors-label="eligibleVisitorsLabel = $event"
                        @eligible-visitors-updated="$emit('eligibleVisitorsUpdated', $event)" />
                </div>
            </template>
            <template #usedBy>
                <segment-dependency-list
                    open-segments-in-new-tab
                    :dependents-loading="dependentsLoading"
                    :segments="[segment]"
                    :segment-dependents="segmentDependents" />
            </template>
        </pendo-tabs>
    </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import { PendoIcon, PendoTabs, PendoTooltip } from '@pendo/components';
import EligibleVisitorsList from '@/segments/components/segment-editor-vue/components/eligible-visitors-list';
import SegmentDependencyList from '@/common/segment-dependency-list/segment-dependency-list.vue';
import { formatNumberWithSuffix } from '@/utils/formatters';

export default {
    name: 'SegmentMetricsPanel',
    components: {
        EligibleVisitorsList,
        PendoIcon,
        PendoTabs,
        SegmentDependencyList,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        isValid: {
            type: Boolean,
            required: true,
        },
        segment: {
            type: Object,
            required: true,
        },
        showGuideEligibility: {
            type: Boolean,
            default: true,
        },
        showDependenciesTab: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['eligibleVisitorsUpdated'],
    data () {
        return {
            activeTab: 'eligibleVisitors',
            eligibleVisitorsLabel: '',
        };
    },
    computed: {
        ...mapGetters({
            getSegmentGuideEligibility: 'segments/getSegmentGuideEligibility',
            countSegmentDependents: 'segments/countSegmentDependents',
            areSegmentDependentsLoading: 'segments/areSegmentDependentsLoading',
        }),
        ...mapState({
            segmentDependents: (state) => state.segments.segmentDependents,
        }),
        tabOptions () {
            return [
                {
                    prop: 'eligibleVisitors',
                    label: this.eligibleVisitorsLabel,
                },
                {
                    prop: 'usedBy',
                    label: this.usedByLabel,
                    lazy: true,
                },
            ];
        },
        usedByLabel () {
            const count = this.countSegmentDependents(this.segment);

            if (count < 0) return this.$t('segmentMetricsPanel.Used by');

            return this.$t('segmentMetricsPanel.Used by ({count})', { count: formatNumberWithSuffix(count) });
        },
        guideEligibility () {
            return this.getSegmentGuideEligibility(this.segment);
        },
        guideEligibilityText () {
            if (!this.segment) {
                return '--';
            }

            return this.guideEligibility.eligible ? this.$t('Yes') : this.$t('No');
        },
        guideStatusColor () {
            return this.guideEligibility?.eligible ? 'var(--palette-green-70)' : 'var(--palette-red-70)';
        },
        dependentsLoading () {
            return this.areSegmentDependentsLoading([this.segment]);
        },
    },
    watch: {
        activeTab (newActiveTab) {
            if (newActiveTab === 'usedBy') {
                this.loadDependents({ segments: [this.segment] });
            }
        },
    },
    methods: {
        ...mapActions({
            loadDependents: 'segments/loadDependents',
        }),
    },
};
</script>

<style scoped lang="less">
.segment-metrics-panel {
    height: 100%;
    background-color: var(--palette-gray-0);
    border: 1px solid var(--palette-gray-40);
    border-radius: 3px;
    display: flex;
    flex-direction: column;
    max-height: 100%;
}
.guide-eligibility {
    font-weight: 600;
    font-size: 22px;
    line-height: 22px;
    margin-right: 8px;
    padding: 16px;

    &-text {
        font-weight: 400;
        font-size: 14px;
    }
}

.eligible-visitors-info {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
}

.guide-eligibility-label,
.eligible-visitors-label {
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 22px;
    display: flex;
    align-items: center;
    margin: 0;
    padding: 0;
    gap: 8px;
}
.guide-eligibility-wrapper {
    display: flex;
    gap: 10px;
    align-items: center;
    padding-top: 8px;
}

.eligible-visitors-list-wrapper {
    overflow-y: auto;
    height: 100%;
}

.eligible-visitors-info-tooltip {
    .tooltip-inner {
        max-width: unset !important;
        ul {
            padding-inline-start: 20px;
            margin: 0;
        }
    }
}
.eligible-visitors-wrapper {
    display: grid;
    grid-template-rows: auto 1fr;
    overflow: auto;
    padding: 0 16px 16px;
    margin-top: 16px;
    height: 100%;

    &.top-padding {
        padding-top: 16px;
    }
}
:deep(.pendo-tabs-nav__list) {
    overflow: scroll;
    padding: 0 16px;
}

:deep(.pendo-tabs-content) {
    overflow-y: auto;
    padding: 16px;
    height: 100%;

    .eligible-visitors-text {
        margin-top: 24px;
        padding-top: 0;
    }
}
:deep(.pendo-tabs-nav__tab) {
    margin-left: 0;
}
:deep(.segment-dependents) {
    margin-top: 0;
}
:deep(#pendo-tab-panel-eligibleVisitors) {
    height: 100%;
}
</style>
